// src/pages/machines/MachineForm.jsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection, getDocs, query, where,
  addDoc, updateDoc, doc, getDoc, serverTimestamp, arrayUnion,
} from "firebase/firestore";
import { db } from "../../firebase";
import { fetchClientsWithSites, autoSelectSiteIfOnlyOne } from "../../services/clientService";
import { CAPACITY_UNITS, defaultCapacityUnitForType } from "../../utils/machineCapacity";

const getUser = () => JSON.parse(localStorage.getItem("user") || "{}");

// "VRF" is stored as-is; the dropdown shows it as "VRF / VRV" because VRV is
// Daikin's brand name for the same technology — clients use both words.
const MACHINE_TYPES = ["VRF", "Split AC", "Cassette AC", "Chiller", "AHU", "FCU", "Ducted AC", "Other"];
const BRANDS        = ["Daikin", "Voltas", "Blue Star", "Carrier", "Hitachi", "LG", "Samsung", "Mitsubishi", "Trane", "Other"];
const STATUSES      = [
  { value: "working",      label: "Working" },
  { value: "under_repair", label: "Under Repair" },
  { value: "breakdown",    label: "Breakdown" },
  { value: "inactive",     label: "Inactive" },
];

const EMPTY_FORM = {
  machine_name:         "",
  machine_type:         "Split AC",
  brand:                "",
  model_no:             "",
  serial_no:            "",
  capacity_value:       "",
  capacity_unit:        "TR",   // matches default machine_type "Split AC"
  installation_date:    "",
  warranty_expiry:      "",
  location_description: "",
  current_status:       "working",
  client_id:            "",
  client_name:          "",
  site_id:              "",
  site_name:            "",
  amc_contract_id:      "",
  notes:                "",
};

export default function MachineForm() {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const isEdit    = Boolean(id);
  const me        = getUser();
  const companyId = me.company_id || "";
  const isTechnician = me.role === "technician";

  const [form,    setForm]    = useState(EMPTY_FORM);
  const [clients, setClients] = useState([]);
  const [sites,   setSites]   = useState([]);
  const [amcs,    setAmcs]    = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [linkedTaskId, setLinkedTaskId] = useState("");
  // Once the user picks a unit themselves (or we're editing a saved machine),
  // changing Machine Type must never silently overwrite their unit choice.
  const [capacityUnitTouched, setCapacityUnitTouched] = useState(isEdit);

  useEffect(() => { loadMasterData(); }, []); // eslint-disable-line

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cId    = params.get("client_id");
    const cName  = params.get("client_name");
    const sId    = params.get("site_id");
    const sName  = params.get("site_name");
    const taskId = params.get("task_id");
    if (cId)    set("client_id",   cId);
    if (cName)  set("client_name", decodeURIComponent(cName));
    if (sId)    set("site_id",     sId);
    if (sName)  set("site_name",   decodeURIComponent(sName));
    if (taskId) setLinkedTaskId(taskId);
  }, []); // eslint-disable-line

  // Client dropdown only shows clients that have at least one site (SOT check) —
  // prevents registering a machine under a client with no site to attach it to.
  const loadMasterData = async () => {
    try {
      const [clientsWithSites, sSnap, aSnap] = await Promise.all([
        fetchClientsWithSites(companyId),
        getDocs(query(collection(db, "sites"),         where("company_id", "==", companyId))),
        getDocs(query(collection(db, "amc_contracts"), where("company_id", "==", companyId))),
      ]);
      setClients(clientsWithSites);
      setSites(sSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAmcs(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      if (isEdit) {
        const snap = await getDoc(doc(db, "site_machines", id));
        if (snap.exists()) {
          const data = snap.data();
          if (data.company_id !== companyId) {
            alert("Access denied — this machine belongs to a different company");
            navigate("/machines");
            return;
          }
          setForm({
            machine_name:         data.machine_name         || "",
            machine_type:         data.machine_type         || "Split AC",
            brand:                data.brand                || "",
            model_no:             data.model_no             || "",
            serial_no:            data.serial_no            || "",
            // New schema: capacity_value + capacity_unit.
            // Legacy docs only have capacity_tr — load it as a TR value.
            capacity_value:       data.capacity_value ?? data.capacity_tr ?? "",
            capacity_unit:        data.capacity_unit || (data.capacity_tr ? "TR" : defaultCapacityUnitForType(data.machine_type)),
            installation_date:    data.installation_date    || "",
            warranty_expiry:      data.warranty_expiry      || "",
            location_description: data.location_description || "",
            current_status:       data.current_status       || "working",
            client_id:            data.client_id            || "",
            client_name:          data.client_name          || "",
            site_id:              data.site_id              || "",
            site_name:            data.site_name            || "",
            amc_contract_id:      data.amc_contract_id      || "",
            notes:                data.notes                || "",
          });
        }
        setLoading(false);
      }
    } catch (err) {
      console.error("MachineForm loadMasterData error:", err);
      setLoading(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Pre-selects the unit this machine type is normally rated in
  // (VRF -> HP, everything else -> TR), but only until the user has
  // picked a unit themselves.
  const handleMachineTypeChange = (machineType) => {
    set("machine_type", machineType);
    if (!capacityUnitTouched) set("capacity_unit", defaultCapacityUnitForType(machineType));
  };

  // Auto-selects the site if the client has exactly one — saves the operator
  // a step for the common single-site case.
  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    set("client_id",   clientId);
    set("client_name", client?.client_name || "");
    set("site_id",     "");
    set("site_name",   "");

    const clientSites = sites.filter(s => s.client_id === clientId);
    const onlySite = autoSelectSiteIfOnlyOne(clientSites);
    if (onlySite) {
      set("site_id",   onlySite.id);
      set("site_name", onlySite.site_name);
    }
  };

  const handleSiteChange = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    set("site_id",   siteId);
    set("site_name", site?.site_name || "");
  };

  const filteredSites = form.client_id ? sites.filter(s => s.client_id === form.client_id) : sites;
  const filteredAmcs  = form.site_id   ? amcs.filter(a => a.site_id   === form.site_id)   : amcs;

  const handleSave = async () => {
    if (!form.client_id)           { alert("Client required"); return; }
    if (!form.site_id)             { alert("Site required"); return; }

    // Capacity is optional, but if entered it must be a positive number.
    // String() because an edited machine loads capacity_value as a number.
    const capacityValue = String(form.capacity_value).trim() === "" ? null : Number(form.capacity_value);
    if (capacityValue !== null && (!Number.isFinite(capacityValue) || capacityValue <= 0)) {
      alert("Capacity must be a number greater than 0");
      return;
    }

    // Machine Name is optional. If left blank, build a readable name from the
    // details already entered, so every machine still has a name wherever it
    // is shown (machine list, service jobs, spare parts).
    // Example: "Split AC 1.5 TR - LG (Room 7)"
    const typedName = form.machine_name.trim();
    const brandName = form.brand.trim();
    const locationText = form.location_description.trim();
    const autoName =
      [form.machine_type, capacityValue !== null ? `${capacityValue} ${form.capacity_unit}` : ""]
        .filter(Boolean).join(" ")
      + (brandName ? ` - ${brandName}` : "")
      + (locationText ? ` (${locationText})` : "");
    const machineName = typedName || autoName;

    setSaving(true);
    try {
      const payload = {
        company_id:           companyId,
        machine_name:         machineName,
        machine_type:         form.machine_type,
        brand:                form.brand.trim(),
        model_no:             form.model_no.trim(),
        serial_no:            form.serial_no.trim(),
        // Stored exactly as on the nameplate (value + unit), never converted.
        // capacity_tr is written ONLY for TR machines so any older reader keeps
        // working; for HP/kW it is null so nothing can misread "10 HP" as "10 TR".
        // On edit, switching TR -> HP correctly clears the old capacity_tr.
        capacity_value:       capacityValue,
        capacity_unit:        capacityValue !== null ? form.capacity_unit : null,
        capacity_tr:          capacityValue !== null && form.capacity_unit === "TR" ? capacityValue : null,
        installation_date:    form.installation_date || null,
        warranty_expiry:      form.warranty_expiry   || null,
        location_description: form.location_description.trim(),
        current_status:       form.current_status,
        client_id:            form.client_id,
        client_name:          form.client_name,
        site_id:              form.site_id,
        site_name:            form.site_name,
        amc_contract_id:      form.amc_contract_id || null,
        notes:                form.notes.trim(),
        updated_at:           serverTimestamp(),
      };

      if (isEdit) {
        await updateDoc(doc(db, "site_machines", id), payload);
        alert("✅ Machine updated");
      } else {
        payload.created_by      = me.uid || me.id || "";
        payload.created_by_name = me.name || "";
        payload.created_at      = serverTimestamp();
        const newMachineRef = await addDoc(collection(db, "site_machines"), payload);

        // Agar ye machine kisi service job se navigate karke add hui hai,
        // toh us job ke machine_ids array mein link karo
        if (linkedTaskId) {
          try {
            await updateDoc(doc(db, "service_tasks", linkedTaskId), {
              machine_ids: arrayUnion(newMachineRef.id),
              updated_at:  serverTimestamp(),
            });
          } catch (linkErr) {
            console.warn("Machine-to-job linking failed (non-blocking):", linkErr.message);
          }
        }

        alert("✅ Machine added");
      }
      navigate(-1);
    } catch (err) {
      console.error("MachineForm save error:", err);
      alert("Failed to save: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
      <div style={{ fontSize: "28px", marginBottom: "10px" }}>⏳</div>
      Loading...
    </div>
  );

  return (
    <div style={{ padding: "20px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ maxWidth: "680px", margin: "0 auto" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
          <button onClick={() => navigate(-1)}
            style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: "22px", padding: "4px" }}>
            ←
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: "#0f172a" }}>
              {isEdit ? "Edit Machine" : "Add Machine"}
            </h2>
            <p style={{ margin: "2px 0 0", color: "#94a3b8", fontSize: "13px" }}>
              {isEdit ? "Update installed machine details" : "Register an installed AC / VRF unit at a site"}
            </p>
          </div>
        </div>

        {/* FORM CARD */}
        <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>

          {/* LOCATION */}
          <Section title="📍 Location">
            <Row2>
              <Field label="Client *">
                <select value={form.client_id} onChange={e => handleClientChange(e.target.value)} style={selectStyle}>
                  <option value="">Select Client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                </select>
              </Field>
              <Field label="Site *">
                <select value={form.site_id} onChange={e => handleSiteChange(e.target.value)} style={selectStyle} disabled={!form.client_id}>
                  <option value="">Select Site</option>
                  {filteredSites.map(s => <option key={s.id} value={s.id}>{s.site_name}</option>)}
                </select>
                {filteredSites.length === 1 && form.site_id === filteredSites[0].id && (
                  <p style={{ fontSize: "12px", color: "#059669", marginTop: "4px", fontWeight: "600" }}>
                    ✓ Auto-selected (only site for this client)
                  </p>
                )}
              </Field>
            </Row2>
            <Field label="Location Description (Floor, Room, Zone)">
              <input value={form.location_description} onChange={e => set("location_description", e.target.value)}
                placeholder="e.g. Server Room - North Wall, 2nd Floor" style={inputStyle} />
            </Field>
          </Section>

          {/* MACHINE DETAILS */}
          <Section title="🏭 Machine Details">
            <Row2>
              <Field label="Machine Name">
                <input value={form.machine_name} onChange={e => set("machine_name", e.target.value)}
                  placeholder="Optional — auto-named if left blank" style={inputStyle} />
              </Field>
              <Field label="Machine Type">
                <select value={form.machine_type} onChange={e => handleMachineTypeChange(e.target.value)} style={selectStyle}>
                  {MACHINE_TYPES.map(t => <option key={t} value={t}>{t === "VRF" ? "VRF / VRV" : t}</option>)}
                </select>
              </Field>
            </Row2>
            <Row2>
              <Field label="Brand">
                <select value={form.brand} onChange={e => set("brand", e.target.value)} style={selectStyle}>
                  <option value="">Select Brand</option>
                  {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Capacity">
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="number" step="any" min="0" value={form.capacity_value}
                    onChange={e => set("capacity_value", e.target.value)}
                    placeholder={form.capacity_unit === "HP" ? "e.g. 10" : form.capacity_unit === "kW" ? "e.g. 22.4" : "e.g. 1.5"}
                    style={{ ...inputStyle, flex: 2, minWidth: 0 }} />
                  <select value={form.capacity_unit}
                    onChange={e => { set("capacity_unit", e.target.value); setCapacityUnitTouched(true); }}
                    style={{ ...selectStyle, flex: 1, minWidth: 0 }}>
                    {CAPACITY_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
              </Field>
            </Row2>
            <Row2>
              <Field label="Model No.">
                <input value={form.model_no} onChange={e => set("model_no", e.target.value)}
                  placeholder="e.g. FTKF50TV" style={inputStyle} />
              </Field>
              <Field label="Serial No.">
                <input value={form.serial_no} onChange={e => set("serial_no", e.target.value)}
                  placeholder="e.g. DK2024XXXXXX" style={inputStyle} />
              </Field>
            </Row2>
          </Section>

          {/* DATES & WARRANTY — sirf non-technician ke liye */}
          {!isTechnician && (
            <Section title="📅 Dates & Warranty">
              <Row2>
                <Field label="Installation Date">
                  <input type="date" value={form.installation_date}
                    onChange={e => set("installation_date", e.target.value)} style={inputStyle} />
                </Field>
                <Field label="Warranty Expiry">
                  <input type="date" value={form.warranty_expiry}
                    onChange={e => set("warranty_expiry", e.target.value)} style={inputStyle} />
                </Field>
              </Row2>
            </Section>
          )}

          {/* STATUS & AMC — sirf non-technician ke liye */}
          {!isTechnician && (
            <Section title="⚙️ Status & AMC">
              <Row2>
                <Field label="Current Status">
                  <select value={form.current_status} onChange={e => set("current_status", e.target.value)} style={selectStyle}>
                    {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Linked AMC Contract">
                  <select value={form.amc_contract_id} onChange={e => set("amc_contract_id", e.target.value)} style={selectStyle}>
                    <option value="">None</option>
                    {filteredAmcs.map(a => <option key={a.id} value={a.id}>{a.contract_no} — {a.client_name}</option>)}
                  </select>
                </Field>
              </Row2>
            </Section>
          )}

          {/* NOTES */}
          <Section title="📝 Notes">
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)}
              placeholder="Any additional notes about this machine..."
              rows={3}
              style={{ ...inputStyle, resize: "vertical", lineHeight: "1.5" }} />
          </Section>

          {/* BUTTONS */}
          <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
            <button onClick={() => navigate(-1)}
              style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontWeight: "600", fontSize: "14px", cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{
                flex: 2, padding: "13px", borderRadius: "12px", border: "none",
                background: saving ? "#e2e8f0" : "linear-gradient(135deg, #0ea5e9, #6366f1)",
                color: saving ? "#94a3b8" : "#fff", fontWeight: "700", fontSize: "14px",
                cursor: saving ? "not-allowed" : "pointer",
                boxShadow: saving ? "none" : "0 4px 12px rgba(99,102,241,0.3)",
              }}>
              {saving ? "Saving..." : isEdit ? "Update Machine" : "Add Machine"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: "24px" }}>
      <p style={{ margin: "0 0 12px", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>{children}</div>
    </div>
  );
}
function Row2({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>{children}</div>;
}
function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "5px" }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px", border: "1.5px solid #e2e8f0",
  borderRadius: "10px", fontSize: "13px", background: "#f8fafc",
  outline: "none", boxSizing: "border-box", color: "#0f172a",
};
const selectStyle = { ...inputStyle, cursor: "pointer" };
